import React, { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import Tealium from "@4tn/webx-analytics";
import { styled } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import Icon from "@common/Icons/Icons";
import { IconEnum, maxPageContentWidth } from "@constants/consts";
import { colors, fonts, gradients } from "@constants/cssVariables";
import { TEALIUM_EVENT_CATEGORY, TEALIUM_EVENT_NAME } from "@constants/tealium.constants";
import { useNoticeError } from "@utils/common/newRelic";
import { fetchByEndpoint } from "@utils/pageContent/fetchData";

const tabBarQueryKey = "tabBar";

export const RIGHT_ARROW_TEST_ID = "bar-right-arrow";
export const LEFT_ARROW_TEST_ID = "bar-left-arrow";

export const TabBarContainer = styled("div")<{ isTabBarHidden?: boolean }>(({ isTabBarHidden, theme }) => ({
  position: "absolute",
  zIndex: theme.zIndex.appBar - 1,
  height: 56,
  top: 0,
  width: "100%",
  backgroundColor: colors.tabBar,
  marginTop: theme.spacing(14),
  paddingRight: 0,
  transition: "transform 150ms ease-in",
  transform: isTabBarHidden ? "translateY(-112px)" : "none",

  [theme.breakpoints.up("md")]: {
    transform: isTabBarHidden ? "translateY(-120px)" : "none",

    marginTop: theme.spacing(16),
  },
}));

const ItemsGradientContainer = styled("div")(({ theme }) => ({
  height: "100%",
  width: "100%",
  margin: "0 auto",
  position: "relative",
  maxWidth: maxPageContentWidth,

  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(0, 8),
  },

  [theme.breakpoints.up("lg")]: {
    justifyContent: "flex-start",
    padding: theme.spacing(0, 24),
  },
}));

export const NavigationItem = styled(Link)(({ theme }) => ({
  display: "inline-flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  color: colors.neutral0,
  textDecoration: "none",
  font: fonts.tab,
  whiteSpace: "nowrap",
  borderBottom: "3px solid transparent",
  transition: "all 0.3s",
  cursor: "pointer",

  "&[aria-current=true]": {
    borderBottom: "3px solid white",
    fontWeight: "bold",
  },

  "&:after": {
    content: 'attr(data-text) / "";',
    height: 0,
    visibility: "hidden",
    overflow: "hidden",
    userSelect: "none",
    pointerEvents: "none",
    fontWeight: "bold",
  },

  [theme.breakpoints.up("lg")]: {
    "&:hover": {
      borderBottom: "3px solid white",
    },
  },

  [theme.breakpoints.down("md")]: {
    [":first-of-type"]: {
      marginLeft: theme.spacing(4),
    },
  },
}));

export const ItemsContainer = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  color: colors.neutral0,
  justifyContent: "flex-start",
  marginLeft: 0,
  gap: theme.spacing(6),
  overflowX: "auto",
  [`::-webkit-scrollbar`]: {
    display: "none",
  },
  MsOverflowStyle: "none", // Edge
  scrollbarWidth: "none", // Firefox

  "a:first-of-type": {
    paddingLeft: 0,
  },

  [theme.breakpoints.up("md")]: {
    gap: theme.spacing(8),
  },

  [theme.breakpoints.down("md")]: {
    paddingRight: theme.spacing(4),
  },
}));

const GradientDiv = styled("div")(({ theme }) => ({
  position: "absolute",
  height: "100%",
  zIndex: 3,
  top: 0,
  width: 80,

  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const LeftGradient = styled(GradientDiv)(({ theme }) => ({
  left: 0,
  background: `${gradients.fadePrimary90Left} 100% 100%;`,

  [theme.breakpoints.up("lg")]: {
    left: 96,
  },
}));

const RightGradient = styled(GradientDiv)(({ theme }) => ({
  right: 0,
  background: `${gradients.fadePrimary90Right} 100% 100%;`,

  [theme.breakpoints.up("lg")]: {
    right: 96,
  },
}));

const Arrow = styled(Icon, { shouldForwardProp: (prop) => prop !== "isShown" })<{
  isShown: boolean;
}>(({ theme, isShown }) => ({
  zIndex: theme.zIndex.appBar,
  color: colors.neutral0,
  position: "absolute",
  top: 14,
  cursor: "pointer",
  height: 28,
  width: 28,
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  visibility: isShown ? "visible" : "hidden",
  border: `2px solid ${colors.neutral0}`,
  borderRadius: "26px",

  "div:first-of-type": {
    height: "16px",
    width: "16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    svg: {
      transform: "translateX(1px)",
    },
  },

  "&:active": {
    backgroundColor: colors.primary120,
    border: `2px solid ${colors.primary120}`,
  },

  [theme.breakpoints.down("lg")]: {
    display: "none",
  },
}));

const LeftArrow = styled(Arrow)(() => ({
  left: 20,
}));

const RightArrow = styled(Arrow)(() => ({
  right: 20,
}));

const trackClick = (label: string) => {
  Tealium.link({
    event_category: TEALIUM_EVENT_CATEGORY.HEADER,
    event_name: TEALIUM_EVENT_NAME.HEADER_CLICK,
    event_label: label,
  });
};

const TabBar: React.FC<{ navigation?: Navigation | null }> = ({ navigation }) => {
  const [shouldShowLeftGradient, setShouldShowLeftGradient] = useState(false);
  const [shouldShowRightGradient, setShouldShowRightGradient] = useState(false);
  const [isTabBarHidden, setIsTabBarHidden] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const {
    data: items,
    isError,
    error,
  } = useQuery({
    queryKey: [tabBarQueryKey],
    queryFn: () => fetchByEndpoint<Navigation>("/api/navigation/tabbar-navigation"),
    initialData: navigation,
    enabled: !navigation,
    select: (data) => data?.items,
  });

  useNoticeError(error, { queryKey: tabBarQueryKey });

  const scrollHorizontally = (direction: "left" | "right") => {
    if (!scrollContainerRef.current) return;

    const { clientWidth } = scrollContainerRef.current;
    const scrollBy = direction === "left" ? -clientWidth : clientWidth;
    scrollContainerRef.current.scrollBy({ left: scrollBy, behavior: "smooth" });
  };

  const setSideGradients = () => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

    setShouldShowLeftGradient(scrollLeft > 0);
    setShouldShowRightGradient(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    if (!scrollContainerRef.current) return;

    setSideGradients();
    window.addEventListener("resize", setSideGradients);
    scrollContainerRef.current.addEventListener("scroll", setSideGradients);

    return () => {
      scrollContainerRef.current?.removeEventListener("scroll", setSideGradients);
      window.removeEventListener("resize", setSideGradients);
    };
  }, [scrollContainerRef]);

  useEffect(() => {
    let scrollPosition = window.scrollY;
    const handleScroll = () => {
      setIsTabBarHidden(window.scrollY > 80 && window.scrollY >= scrollPosition);
      scrollPosition = window.scrollY;
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const basePath = useMemo(() => {
    const [pathname] = router.asPath.split("?");
    const [base] = pathname.split("/").filter(Boolean);
    return `/${base || ""}`;
  }, [router.asPath]);

  if (isError && !items) return null;

  return (
    <TabBarContainer isTabBarHidden={isTabBarHidden}>
      <ItemsGradientContainer>
        {shouldShowLeftGradient && (
          <LeftGradient>
            <LeftArrow
              isShown={shouldShowLeftGradient}
              data-testid={LEFT_ARROW_TEST_ID}
              onClick={() => scrollHorizontally("left")}
              icon={IconEnum.LEFT_CHEVRON}
            />
          </LeftGradient>
        )}
        <ItemsContainer ref={scrollContainerRef}>
          {items?.map((item) => (
            <React.Fragment key={item.id}>
              {item.type !== "submenu" && (
                <NavigationItem
                  data-text={item.title}
                  aria-current={basePath === item.url || (basePath !== "/" && item.url.startsWith(basePath))}
                  key={item.id}
                  href={item.url}
                  target={item.target}
                  onClick={() => trackClick(item.title)}
                  suppressHydrationWarning={item.url.startsWith("#")}
                >
                  {item.title}
                </NavigationItem>
              )}
            </React.Fragment>
          ))}
        </ItemsContainer>
        {shouldShowRightGradient && (
          <RightGradient>
            <RightArrow
              isShown={shouldShowRightGradient}
              data-testid={RIGHT_ARROW_TEST_ID}
              onClick={() => scrollHorizontally("right")}
              icon={IconEnum.RIGHT_CHEVRON}
            />
          </RightGradient>
        )}
      </ItemsGradientContainer>
    </TabBarContainer>
  );
};

export default TabBar;
